/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon } from '@/shared/ui/radix-icons';
import { monthGrid, dateKey, isSameDay } from '@features/agenda/utils/agenda-dates';
import { Text, UnstyledButton } from '@/shared/ui';

interface MiniCalendarProps {
  anchor: Date;
  onSelectDate: (date: Date) => void;
}

const WEEKDAY_INDEXES = [0, 1, 2, 3, 4, 5, 6];

/** Single-letter weekday abbreviations (Sun=start, Sat=end). */
const WEEKDAY_1L = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'] as const;

/**
 * Reports whether the given date is today.
 * @param day - The date to test.
 * @returns True when `day` falls on the current calendar day.
 */
const isToday = (day: Date): boolean => dayjs().isSame(dayjs(day), 'day');

/**
 * Inner highlight circle. It fills its cell's height and takes its width from
 * the 1:1 ratio, so the circle is centred on the column no matter how wide the
 * sidebar renders — the old fixed 20px circle inside a wider, gapped column
 * read as off-centre and cramped two-digit days.
 */
const DAY_CIRCLE_BASE =
  'flex aspect-square h-6 shrink-0 items-center justify-center rounded-full text-xs leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--seed-base) focus-visible:ring-offset-1 focus-visible:ring-offset-(--surface-primary)';

/**
 * Resolves the visual treatment for a mini-calendar day so the three states
 * stay mutually distinguishable: filled selection, outlined today, and a
 * filled-plus-ring "today is selected" combination.
 *
 * @param selected Whether the cell is the active anchor day.
 * @param today Whether the cell is the current calendar day.
 * @returns Tailwind class string for the day cell state.
 */
const dayStateClass = (selected: boolean, today: boolean): string => {
  if (selected && today) {
    return 'bg-(--seed-base) [font-weight:var(--font-bold)] text-(--text-inverse) ring-1 ring-(--seed-base) ring-offset-1 ring-offset-(--surface-primary)';
  }
  if (selected) {
    return 'bg-(--seed-base) [font-weight:var(--font-bold)] text-(--text-inverse)';
  }
  if (today) {
    return 'ring-1 ring-(--seed-base) [font-weight:var(--font-bold)] text-(--seed-base) hover:bg-(--surface-secondary)';
  }
  return 'text-(--text-primary) hover:bg-(--surface-secondary)';
};

/**
 * Compact, navigable month preview shown above the calendar filters. Browsing
 * months is local to this widget; clicking a current-month day selects it and
 * navigates the main agenda via `onSelectDate`.
 *
 * @param props Selected anchor date and the day-selection handler.
 * @returns Rendered mini-calendar.
 */
export const MiniCalendar: React.FC<MiniCalendarProps> = ({
  anchor,
  onSelectDate,
}) => {
  const { t } = useTranslation('features/agenda');
  const [view, setView] = useState(() => ({
    year: anchor.getFullYear(),
    month: anchor.getMonth(),
  }));

  useEffect(() => {
    setView({ year: anchor.getFullYear(), month: anchor.getMonth() });
  }, [anchor]);

  const shiftMonth = (direction: 1 | -1): void => {
    const next = dayjs(new Date(view.year, view.month, 1)).add(
      direction,
      'month'
    );
    setView({ year: next.year(), month: next.month() });
  };

  const cells = monthGrid(view.year, view.month);
  const title = `${t(`month_long.${view.month}`)} de ${view.year}`;

  return (
    <div
      data-testid="agenda-mini-calendar"
      className="w-full rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-primary)] p-(--space-3)"
    >
      <div data-testid="mini-cal-header" className="mb-(--space-2) grid w-full grid-cols-7 items-center">
        <UnstyledButton
          type="button"
          aria-label={t('mini.previous')}
          onClick={() => shiftMonth(-1)}
          className="flex h-7 w-7 items-center justify-center rounded-(--radius-md) text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-secondary)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--seed-base)] focus-visible:ring-offset-1"
        >
          <ChevronLeftIcon />
        </UnstyledButton>
        <Text component="span" className="col-span-5 text-center text-(--text-small) [font-weight:var(--font-bold)] text-[var(--text-primary)]">
          {title}
        </Text>
        <UnstyledButton
          type="button"
          aria-label={t('mini.next')}
          onClick={() => shiftMonth(1)}
          className="flex h-7 w-7 items-center justify-center rounded-(--radius-md) text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-secondary)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--seed-base)] focus-visible:ring-offset-1"
        >
          <ChevronRightIcon />
        </UnstyledButton>
      </div>
      <div className="grid grid-cols-7 gap-y-(--space-1) justify-items-center">
        {WEEKDAY_INDEXES.map((i) => (
          <div
            key={i}
            data-testid="mini-cal-weekday-header"
            className="flex h-6 w-full items-center justify-center text-xs [font-weight:var(--font-semibold)] uppercase text-[var(--text-tertiary)]"
          >
            {WEEKDAY_1L[i]}
          </div>
        ))}
        {cells.map((cell) => {
          if (!cell.isCurrentMonth) {
            return (
              <Text component="span"
                key={dateKey(cell.date)}
                aria-hidden="true"
                className="block h-6 w-full"
              />
            );
          }
          const selected = isSameDay(anchor.toISOString(), cell.date);
          const today = isToday(cell.date);
          const stateClass = dayStateClass(selected, today);
          return (
            <UnstyledButton
              key={dateKey(cell.date)}
              type="button"
              aria-pressed={selected}
              onClick={() => onSelectDate(cell.date)}
              className="flex h-6 w-full items-center justify-center"
            >
              <div className={`${DAY_CIRCLE_BASE} ${stateClass}`}>
                {cell.date.getDate()}
              </div>
            </UnstyledButton>
          );
        })}
      </div>
    </div>
  );
};
